<template>
  <div class="d-flex flex-column main-container">
    <CommonLoader />
    <!--
        Error page to be shown to the end users.
  
        =====
        Sorry!
        Something went wrong.
        We are working on it.
        ====
  
        This section is hidden in most of the flows, but in some flows, it is shown to the user.
      -->
    <template v-if="bShowErrorPageToEndUser">
      <CommonHeader />
      <div class="error-wrapper">
        <div class="d-flex justify-content-center">
          <div
            class="text-center wrapper d-flex flex-column align-items-center w-100"
          >
            <h1>
              {{ $t('ERROR_PAGE.TITLE') }}
            </h1>
            <div class="work-in-progress">
              <h2 class="mb-4 heading2 pt-0">
                {{ $t('ERROR_PAGE.HEADING') }}
              </h2>
              <div class="working-on-it">
                {{ $t('ERROR_PAGE.MESSAGE') }}
              </div>
              <a
                class="mt-2 d-block"
                href="javascript:void(0)"
                @click="navigateToHome"
                >{{ $t('ERROR_PAGE.BACK_TO_HOME') }}
              </a>
            </div>

            <!-- 
                Additional Debugging data. 
                Only shown on local & Thor environments 
              -->
            <aside v-if="bShowErrorDetailsToEndUser" class="text-left">
              <h3>Error details</h3>
              <p v-if="error?.statusCode">
                Status Code: <code>{{ error.statusCode }}</code>
              </p>
              <p v-if="error?.statusMessage">
                Status Message: <code>{{ error.statusMessage }}</code>
              </p>
              <p v-if="error?.message">
                Error Message: <code>{{ error.message }}</code>
              </p>
              <p v-if="errorAdditionalPayload">
                Error additional payload: <br />
                <code>{{ errorAdditionalPayload }}</code>
              </p>
              <div v-if="error?.stack">
                Error stack trace: <br />
                <div class="error-stack" v-html="error.stack" />
              </div>
            </aside>
          </div>
        </div>
      </div>
      <CommonFooter />
    </template>
  </div>
</template>
<script setup lang="ts">
// IMPORTS
import type { NuxtError } from '#app';

// Composables
const appConfig = useRuntimeConfig();

// Get the error from Nuxt
const props = defineProps<{
  error: NuxtError<any>;
}>();

/**
 * Initialize the APP in both CSR and SSR modes.
 *
 * Note: The same code is also being used in the following pages:
 * - Class Invitation Accept page.
 *  - Path: /pages/invitation/[schoolId]/class_enrollment/[classId]/[invitationId]/accept
 * - Class Invitation Success page.
 *  - Path: /pages/invitation/success
 */

/**
 * Handling for the SSR Mode:
 *
 * Load the user data from the session on the server into the Authenticate Store.
 *
 * Also, activate the user's theme is activated by injecting the theme variables into
 * the head of the page.
 *
 * Also, store the above user data and theme into the Nuxt Async Data, so that it can
 * be retrieved in the CSR mode.
 */
if (import.meta.server) {
  const event = useRequestEvent();
  const userSession = event?.context.session;

  if (userSession) {
    const userData = {
      email: userSession.email,
      userId: userSession.extUserId,
      role: userSession.role,
      firstName: userSession.firstName,
      lastName: userSession.lastName,
      isLtiSession: userSession.launchType === 'lti',
      integrationName: userSession.integrationName || ''
    };

    // Load user data into the Authenticate Store
    useAuthenticateStore().load(userData);

    // Activate theme on SSR from session
    const themeVariables = useThemeService().activateTheme(userSession.theme);

    // Set the theme in the head
    useHead({
      style: [
        {
          innerHTML: themeVariables,
          id: 'theme-variables'
        }
      ]
    });

    // Store the user data and theme in the Nuxt Async Data
    await Promise.all([
      useAsyncData('authenticate-data', () => Promise.resolve(userData)),
      useAsyncData('theme', () => Promise.resolve(userSession.theme))
    ]);
  }
}

/**
 * Handling for the CSR Mode:
 *
 * Retrieve the user data from the Nuxt Async data and load it into the Authenticate Store.
 * Also, retrieve the theme from the Nuxt Async data and activate the theme.
 */
if (import.meta.client) {
  // Extract the user data from the authenticate-data
  const { data } = useNuxtData('authenticate-data');

  if (data.value) {
    // Load it into the Authenticate Store
    useAuthenticateStore().load(data.value);
  }

  // Extract the theme from the theme data
  const { data: theme } = useNuxtData('theme');

  // Activate theme
  const themeVariables = useThemeService().activateTheme(theme.value);

  // Set the theme in the head
  useHead({
    style: [
      {
        innerHTML: themeVariables,
        id: 'theme-variables'
      }
    ]
  });
}

/**
 * Check if the current route is an error page and show the
 * error UI
 *
 * - The user can land on this error page from other MicroApps
 *  such as NLP, Class, & Admin.
 */
const isErrorPage =
  props.error?.url === '/error' || props.error?.url === '/dashboard/error';

// Set the status code to 206 for error page (Setting 200 is not supported by NUXT)
// By default, 404 status code is sent by the Nuxt
if (process.server && isErrorPage) {
  const event = useRequestEvent();
  setResponseStatus(event, 206);
}

// Template variables
const errorData = shallowRef<any>(
  typeof props.error?.data === 'string'
    ? JSON.parse(props.error.data)
    : props.error?.data
);
/**
 * SPECIAL SCENARIOS for DISPLAYING ERROR PAGE
 * TO END USERS
 *
 * Only in a few specific scenarios, we may want to show
 * error page to the end users. These are rare cases
 *
 */
const bShowErrorPageToEndUser = shallowRef<boolean>(
  isErrorPage || errorData.value?.errorHandlingStrategy === 'showErrorPage'
);
/**
 * SPECIAL SCENARIOS for DISPLAYING ERROR INFO
 * TO END USERS
 *
 * Only on specific lower environments, we want to show
 * error details to the end users.
 *
 */
const bShowErrorDetailsToEndUser = shallowRef<boolean>(
  !isErrorPage &&
    (appConfig.public.appEnv === 'local' || appConfig.public.appEnv === 'thor')
);
// Contains additional payload that might be present on the error object
const errorAdditionalPayload = shallowRef<string>('');

// Stringify the additional payload if it is present, so it can be shown in the UI
if (bShowErrorDetailsToEndUser.value && errorData.value) {
  errorAdditionalPayload.value = JSON.stringify(errorData.value);

  if (errorAdditionalPayload.value === '{}') {
    errorAdditionalPayload.value = '';
  }
}

/**
 * Lifecycle hook to run the below code in the browser
 * - It is used to log the error in the browser console
 * - It is used to redirect them to the dashboard OR show the error page to the end users
 */
onMounted(() => {
  // If it is an error page, simply return to show the error page
  if (isErrorPage) {
    return;
  }

  // Log the error in the console
  console.error('Got an error: ', props.error?.message);
  console.error(props.error?.stack);

  // If strategy is to show the error page for help in debugging, then return
  if (sessionStorage.getItem('errorHandlingStrategy') === 'showErrorPage') {
    bShowErrorPageToEndUser.value = true;
    bShowErrorDetailsToEndUser.value = true;
    return;
  }

  /**
   * Proceed as per the error handling strategy
   * - Default is to redirect to the dashboard
   */
  if (errorData.value?.errorHandlingStrategy === 'showLoader') {
    showOverlayLoader();
  } else if (
    !errorData.value?.errorHandlingStrategy ||
    errorData.value?.errorHandlingStrategy === 'redirectToDashboard'
  ) {
    navigateToHome();
  }
});

const navigateToHome = () => {
  showOverlayLoader();
  useNavigation().redirectToUserDashboard();
};
</script>

<style lang="scss" scoped>
/* purgecss start ignore */
.main-container {
  height: 100%;

  .error-wrapper {
    flex-grow: 1;

    .wrapper {
      margin: 130px auto 30px;

      @media (min-width: 768px) {
        max-width: 50%;
      }
      @media (min-width: 992px) {
        max-width: 32%;
      }

      h1 {
        font-size: 32px;
        line-height: 40px;
        font-weight: 300;
        margin-bottom: 0;

        @include media-breakpoint-up(sm) {
          font-size: 40px;
          line-height: 48px;
        }
        @include media-breakpoint-up(lg) {
          font-size: 48px;
          line-height: 56px;
          font-weight: 200;
        }
      }
      h2 {
        margin-bottom: 16px;
        padding-top: 24px;
        font-size: 21px;
        line-height: 32px;
        font-weight: 800;
        color: var(--cup-general-color-5);
      }
      h3 {
        padding-top: 32px;
      }

      .work-in-progress {
        box-shadow: -3px 0px 24px -4px rgba(0, 0, 0, 0.2);
        min-height: 250px;
        padding: 16px;
        margin: 20px 0px;

        @include media-breakpoint-up(md) {
          padding: 24px;
        }
        @include media-breakpoint-up(lg) {
          padding: 24px 32px;
        }
      }

      .working-on-it {
        padding-bottom: 16px;
      }
    }

    aside {
      box-shadow: -3px 0px 24px -4px rgba(0, 0, 0, 0.2);
      padding: 16px;
      padding-bottom: 8px;
      max-width: 650px;

      .error-stack {
        overflow-x: auto;
      }
    }
  }
}
/* purgecss end ignore */
</style>
